import React, { useState } from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDocuments } from "../../context/DocumentContext";
import DataTable from "../../components/common/DataTables.jsx";
import TableActionButtons from "../../components/common/TableActionButtons";
import PageHeader from "../../components/common/PageHeader";
import { DocumentIcon, TrashIcon } from "@heroicons/react/24/outline";
import { API_BASE_URL } from "../../api/axiosConfig";
import { formatDate } from "../../utils/dateUtils";

const shortText = (s, n = 40) => (s ? (s.length > n ? s.slice(0, n) + "…" : s) : "—");

const DocumentListView = ({ searchTerm = "", filterProject = "" }) => {
  const { documents, deleteDocument } = useDocuments();
  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDelete = async (id) => {
    if (typeof deleteDocument === "function") {
      await deleteDocument(id);
      toast.success("Document deleted successfully!");
    }
    setConfirmDelete(null);
  };

  const filteredDocs = documents.filter((doc) => {
    const nameMatch = doc.document_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const descMatch = doc.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const projectMatch = !filterProject || doc.project_name === filterProject;
    return (nameMatch || descMatch) && projectMatch;
  });

  const columns = [
    {
      header: "Document Name",
      accessor: "document_name",
      sortable: true,
      render: (doc) => (
        <div className="flex items-center gap-2">
          <div className="p-1 bg-blue-50 border border-blue-100 rounded text-blue-600">
            <DocumentIcon className="w-4 h-4" />
          </div>
          <div className="font-semibold text-gray-900">{doc.document_name}</div>
        </div>
      )
    },
    {
      header: "Type",
      accessor: "document_type",
      sortable: true,
      render: (doc) => (
        <span className="bg-gray-50 px-2 py-0.5 rounded text-[10px] border border-gray-200 capitalize">
          {doc.document_type || "File"}
        </span>
      )
    },
    {
      header: "Description",
      accessor: "description",
      render: (doc) => <span className="text-gray-500" title={doc.description}>{shortText(doc.description, 35)}</span>
    },
    {
      header: "Project",
      accessor: "project_name",
      sortable: true,
      render: (doc) => (
        doc.project_name ? <span className="text-indigo-600 font-medium">{doc.project_name}</span> : <span className="text-gray-400 italic"><T>General</T></span>
      )
    },
    {
      header: "Uploaded By",
      accessor: "uploader_name",
      sortable: true,
      render: (doc) => (
        <div>
          <div className="text-gray-800">{doc.uploader_name || "Admin"}</div>
          <div className="text-[10px] text-gray-400">{formatDate(doc.created_at)}</div>
        </div>
      )
    },
    {
      header: "Actions",
      align: "right",
      render: (doc) => (
        <TableActionButtons
          onView={doc.file_path ? () => window.open(`${API_BASE_URL}${doc.file_path}`, '_blank', 'noopener,noreferrer') : undefined}
          viewTitle="View / Download"
          onDelete={() => setConfirmDelete(doc)}
        />
      )
    }
  ];

  return (
    <div className="relative">
      <DataTable data={filteredDocs} columns={columns} emptyMessage="No documents found." />

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-xl shadow-lg w-80 text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <TrashIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-base font-semibold mb-2 text-gray-900"><T>Delete Document?</T></h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete <br/>
              <span className="font-bold text-gray-800">"{confirmDelete.document_name}"</span>?
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-1.5 bg-gray-50 border border-gray-300 text-gray-700 rounded hover:bg-gray-100 font-medium text-sm"><T>Cancel</T></button>
              <button onClick={() => handleDelete(confirmDelete.id)} className="px-4 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 font-medium text-sm shadow-sm"><T>Delete</T></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentListView;